import { useState, useEffect } from 'react';
import { Card, CardHeader, CardTitle, CardContent } from '../ui/card';
import { Input } from '../ui/input';
import { Button } from '../ui/button';

const AddressMappings = ({ user, token, api }) => {
  const [mappings, setMappings] = useState([]);
  const [newId, setNewId] = useState('');
  const [newName, setNewName] = useState('');
  const [newAddress, setNewAddress] = useState('');
  const [editingId, setEditingId] = useState(null);
  const [editName, setEditName] = useState('');
  const [editAddress, setEditAddress] = useState('');
  const [error, setError] = useState('');
  const [status, setStatus] = useState('');

  useEffect(() => {
    fetchMappings();
  }, []);

  const fetchMappings = async () => {
    try {
      const data = await api.get('/address-mappings', token);
      setMappings(data);
    } catch (err) {
      setError(err.message);
    }
  };

  const handleAdd = async () => {
    if (!newId.trim()) {
      setError('Customer ID is required');
      return;
    }
    if (newId.includes('|') || newAddress.includes('|')) {
      setError('Pipe characters (|) are not allowed');
      return;
    }
    try {
      setError('');
      const result = await api.post('/address-mappings', {
        customer_id: newId.trim(),
        customer_name: newName.trim(),
        address: newAddress.trim()
      }, token);
      setMappings([...mappings, result].sort((a, b) => a.customer_id.localeCompare(b.customer_id)));
      setNewId('');
      setNewName('');
      setNewAddress('');
      setStatus('Mapping added');
    } catch (err) {
      setError(err.message);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Delete this mapping?')) return;
    try {
      await api.delete(`/address-mappings/${id}`, token);
      setMappings(mappings.filter(m => m.id !== id));
      setStatus('Mapping deleted');
    } catch (err) {
      setError(err.message);
    }
  };

  const startEdit = (mapping) => {
    setEditingId(mapping.id);
    setEditName(mapping.customer_name || '');
    setEditAddress(mapping.address || '');
  };

  const handleSaveEdit = async (mapping) => {
    if (editAddress.includes('|')) {
      setError('Pipe characters (|) are not allowed');
      return;
    }
    try {
      setError('');
      await api.put(`/address-mappings/${mapping.id}`, {
        customer_id: mapping.customer_id,
        customer_name: editName.trim(),
        address: editAddress.trim()
      }, token);
      setMappings(mappings.map(m => m.id === mapping.id ? { ...m, customer_name: editName.trim(), address: editAddress.trim() } : m));
      setEditingId(null);
      setStatus('Mapping updated');
    } catch (err) {
      setError(err.message);
    }
  };

  return (
    <div className="max-w-4xl mx-auto space-y-4">
      <Card>
        <CardHeader className="py-3">
          <CardTitle className="text-base">Address Mappings</CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          {error && <div className="text-sm text-destructive">{error}</div>}
          {status && <div className="text-sm text-emerald-500">{status}</div>}

          {/* Add new mapping */}
          <div className="flex gap-2 flex-wrap sm:flex-nowrap">
            <Input
              value={newId}
              onChange={e => setNewId(e.target.value.toUpperCase())}
              placeholder="Customer ID"
              className="w-full sm:w-28"
            />
            <Input
              value={newName}
              onChange={e => setNewName(e.target.value)}
              placeholder="Customer Name"
              className="w-full sm:w-56"
            />
            <Input
              value={newAddress}
              onChange={e => setNewAddress(e.target.value)}
              placeholder="Street Address (leave blank for remote / no-travel customer)"
              className="flex-1"
            />
            <Button onClick={handleAdd}>Add</Button>
          </div>

          {/* Mappings table */}
          <div className="overflow-x-auto">
          <table className="w-full text-sm min-w-[680px]">
            <thead>
              <tr className="border-b text-left text-muted-foreground">
                <th className="pb-2 w-28">Customer ID</th>
                <th className="pb-2 w-56">Customer Name</th>
                <th className="pb-2">Address</th>
                <th className="pb-2 w-32"></th>
              </tr>
            </thead>
            <tbody>
              {mappings.map(mapping => (
                <tr key={mapping.id} className="border-b border-border/50">
                  <td className="py-2 font-mono">{mapping.customer_id}</td>
                  <td className="py-2 pr-2">
                    {editingId === mapping.id ? (
                      <Input
                        value={editName}
                        onChange={e => setEditName(e.target.value)}
                        onKeyDown={e => e.key === 'Enter' && handleSaveEdit(mapping)}
                        className="h-8"
                        placeholder="Customer name"
                        autoFocus
                      />
                    ) : (
                      mapping.customer_name || <span className="text-muted-foreground italic">—</span>
                    )}
                  </td>
                  <td className="py-2">
                    {editingId === mapping.id ? (
                      <Input
                        value={editAddress}
                        onChange={e => setEditAddress(e.target.value)}
                        onKeyDown={e => e.key === 'Enter' && handleSaveEdit(mapping)}
                        className="h-8"
                      />
                    ) : (
                      mapping.address || <span className="text-muted-foreground italic">remote — no travel</span>
                    )}
                  </td>
                  <td className="py-2 text-right">
                    {editingId === mapping.id ? (
                      <div className="flex gap-1 justify-end">
                        <Button variant="ghost" size="sm" onClick={() => handleSaveEdit(mapping)}>Save</Button>
                        <Button variant="ghost" size="sm" onClick={() => setEditingId(null)}>Cancel</Button>
                      </div>
                    ) : (
                      <div className="flex gap-1 justify-end">
                        <Button variant="ghost" size="sm" onClick={() => startEdit(mapping)}>Edit</Button>
                        <Button variant="ghost" size="sm" onClick={() => handleDelete(mapping.id)}>Delete</Button>
                      </div>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          </div>

          {mappings.length === 0 && (
            <div className="text-sm text-muted-foreground text-center py-4">
              No address mappings yet. Add your home address as "HOM" to get started.
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default AddressMappings;
